import React, { useRef } from 'react'
import { useSearchField } from 'react-aria'
import { useSearchFieldState } from 'react-stately'
import styled from 'styled-components'
import { SvgSearch } from '@chilipiper/icons'
import { SvgRemove } from '@chilipiper/icons/src/design-system'
import { Icon } from '../../../../old/icon'
import { Box } from '../../../core-components'
import { getTextStyleCss } from '../../../core-components/text/helpers'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { FocusableHandlerProps, PositioningProps } from '../../types'
import { IconButtonInternal } from '../../action/icon-button/IconButton'

type Props = {
  INTERNAL_onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  'aria-label': string
  'data-id'?: string
  'data-test-id'?: string
  onChange: (value: string) => void
  onClear?: () => void
  placeholder: string
  value: string
  variant: 'regular' | 'nested'
  size?: 'regular' | 'large'
} & PositioningProps &
  FocusableHandlerProps

export const SearchInput = ({
  onChange,
  onFocus,
  onBlur,
  value,
  'data-id': dataId,
  'data-test-id': dataTestId,
  placeholder,
  'aria-label': ariaLabel,
  onClear,
  variant,
  INTERNAL_onKeyDown,
  size = 'regular',
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'SearchInput')

  const ref = useRef<HTMLInputElement>(null)
  const commonProps = {
    value,
    onChange,
    placeholder,
    onClear,
    onFocus,
    onBlur,
    onKeyDown: INTERNAL_onKeyDown,
  }
  const state = useSearchFieldState(commonProps)
  const {
    clearButtonProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    inputProps: { color: _color, ...inputPropsWithoutColor },
  } = useSearchField(
    {
      ...commonProps,
      'aria-label': ariaLabel,
    },
    state,
    ref
  )
  return (
    <Box w='100%' position='relative' {...positioningProps}>
      <StyledIcon
        icon={SvgSearch}
        color='fg/minor'
        position='absolute'
        left={variant === 'regular' ? 4 : 5}
        top={variant === 'regular' ? 3 : 4}
      />

      <StyledInput
        as='input'
        ref={ref}
        data-id={dataId}
        data-test-id={dataTestId}
        pr={9}
        pl={variant === 'regular' ? '44px' : 9}
        h={variant === 'regular' ? 8 : 9}
        w='100%'
        border='none'
        color='fg/major'
        borderRadius={variant === 'regular' ? 10 : 0}
        variant={variant}
        $size={size}
        {...inputPropsWithoutColor}
      />

      {state.value !== '' && (
        <Box position='absolute' right={3} top={variant === 'regular' ? 2 : 3}>
          <IconButtonInternal
            icon={SvgRemove}
            label='Clear search'
            size={6}
            variant='ghost'
            ariaProps={clearButtonProps}
          />
        </Box>
      )}
    </Box>
  )
}

const StyledIcon = styled(Icon)`
  pointer-events: none;
`

const StyledInput = styled(Box)<{ variant: Props['variant']; $size: 'regular' | 'large' }>`
  ${({ $size }) => getTextStyleCss($size === 'large' ? 'form-value-l' : 'form-value')}
  background: none;
  box-shadow: ${({ theme, variant }) =>
    theme.shadows[variant === 'regular' ? 'border/section' : 'border/row']};

  ::-webkit-search-cancel-button {
    appearance: none;
  }

  :hover {
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};
    box-shadow: ${({ theme }) => theme.shadows['border/row']};
  }

  :focus {
    outline: none;
    background: ${({ theme }) => theme.colors['bg/form-item-focused']};
    box-shadow: ${({ theme }) => theme.shadows['border/form-item-nested-focused']};
  }

  ::placeholder {
    ${({ $size }) => getTextStyleCss($size === 'large' ? 'form-value-l' : 'form-value')}
    color: ${({ theme }) => theme.colors['fg/minor']};
  }
`
